const { Auth } = require('@okta/okta-react')

import axios from 'axios'

import get from 'lodash/get'

import jwtDecode from 'jwt-decode'

import { store, history } from '../app/store'

import routes from '../app/constants/routes'

import { unsetAuth, setAuth } from '../app/auth/actions'

import { createOktaAuthConfig } from '../config/okta-config'

import { OKTA_VERSIONS } from '../app/constants'


const instance = axios.create({
  baseURL: `${OE_ENV.API_GATEWAY_URL}/auth`,
  withCredentials: true
})

const oktaInstanceApiV1 = axios.create({
  baseURL: `${OE_ENV.OKTA_BASE_URL}/api/v1`,
  withCredentials: true
})

const extractDataFromError = (error) => {
  const { response: { data } } = error
  return data
}

const extractErrors = (error) => {
  const { errorSummary, errorCauses, errorCode = '', errorData = {} } = extractDataFromError(error)
  const errorItems = errorCauses.map(errorCause => get(errorCause, 'errorSummary'))
  return { errorCode, errorData, errorSummary, errorItems }
}

const extractValidatorAPiErrors = (error) => {
  const { response: { data: { errors } } } = error
  const errorItems = errors.map(({ message }) => message)
  return { errorSummary: 'There were validation errors', errorItems }
}

const baseStoreOperation = store => prefix => operation => (key, ...args) => store[`${operation}Item`]([prefix, key].join('.'), ...args)

const localStoragePrefixedOperation = baseStoreOperation(localStorage)('AuthIdentityProvider.OKTA')

const storeSetItem = localStoragePrefixedOperation('set')
const storeGetItem = localStoragePrefixedOperation('get')
const storeRemoveItem = localStoragePrefixedOperation('remove')

const getClaims = claims => (claim, fallback = null) => get(claims, `qq:${claim}`, get(claims, claim, fallback))

const setIdentityIntoLocalStorage = (idToken) => {
  const claims = jwtDecode(idToken)
  const getClaim = getClaims(claims)

  const username = getClaim('login')

  const profile = {
    brokerId: getClaim('brokerId'),
    email: getClaim('email'),
    expiration: getClaim('expiresAt'),
    firstName: getClaim('firstName'),
    id: getClaim('id'),
    lastName: getClaim('lastName'),
    permissions: getClaim('scopes', '').split(','),
    phone: getClaim('phone'),
    phoneExt: getClaim('phoneExt'),
    role: getClaim('role', ''),
    status: getClaim('status'), //TODO: This appears to be unused
    title: getClaim('title'),
    username,
  }

  storeSetItem('lastUser', username)
  storeSetItem(`${username}.idToken`, idToken)
  storeSetItem(`${username}.userData`, JSON.stringify(profile))
}

export const getIdentityFromLocalStorage = () => {
  const username = storeGetItem('lastUser')
  return storeGetItem(`${username}.idToken`)
}

export const getUserDataFromLocalStorage = () => {
  const username = storeGetItem('lastUser')
  return JSON.parse(storeGetItem(`${username}.userData`))
}

export const removeIdentityFromLocalStorage = () => {
  const username = storeGetItem('lastUser')
  storeRemoveItem(`${username}.idToken`)
  storeRemoveItem(`${username}.userData`)
}

const onAuthRequired = ({ history }) => {
  let destination = routes.LOGIN
  removeIdentityFromLocalStorage()
  store.dispatch(unsetAuth())
  const { location: { pathname, search } } = history
  if (pathname !== destination) {
    destination += `?redirect=${pathname}${search}`
  }
  history.push(destination)
}

export const oktaAuth = new Auth(createOktaAuthConfig(history, onAuthRequired))


// Signs into OKTA via auth proxy - create profile for localStorage on success
// session is "hidden" in a secure, httpOnly cookie for the api.[env].ownersedge.us domain
// so don't worry about it :)
export const authenticate = async (username, password) => {
  try {
    // const { data: { idToken, status } } = await instance.post('/sign-in', { username, password })
    const { data: { sessionToken } } = await instance.post('/sign-in', { username, password })

    oktaAuth.redirect({ sessionToken })

    return { success: true, error: null }
  } catch (error) {
    return { success: false, error: extractErrors(error) }
  }
}

export const unlock = async (username) => {
  try {
    const { idToken } = await getCurrentSession()
    const data = await instance.post('/unlock-user',
      { username },
      {
        headers: {
          Authorization: idToken
        }
      })
    return { success: true, error: null }
  } catch (error) {
    return { success: false, error: extractErrors(error) }
  }
}

export const getLockedUsers = async (username) => {
  try {
    const { idToken } = await getCurrentSession()
    const data = await instance.get('/get-locked-users',
      {
        headers: {
          Authorization: idToken
        }
      })
    return { success: true, error: null, data }
  } catch (error) {
    return { success: false, error: extractErrors(error) }
  }
}

export const signOut = async () => {
  try {
    console.log('executing signOut...')
    removeIdentityFromLocalStorage()
    await instance.get('/sign-out')
    store.dispatch(unsetAuth())
    return true
  } catch (error) {
    throw error
  }
}

export const signOutIdx = async () => {
  try {
    console.log('executing signOutIdx...')
    removeIdentityFromLocalStorage()
    await oktaInstanceApiV1.delete('/sessions/me')
    // For new Okta Identity Engine, sign-out api call will return delete cookie only response
    await instance.get('/sign-out')
    oktaAuth._oktaAuth.tokenManager.clear()
    store.dispatch(unsetAuth())
    return true
  } catch (error) {
    throw error
  }
}

export const signUp = async (username) => {
  try {
    const { data: { status, reason } } = await instance.post('/sign-up', { username })
    return { status, reason }
  } catch (error) {
    // console.error(error)
  }
  return { status: 'REQUEST_ACCESS', reason: 'REJECTED' }
}

export const forgotPasswordLegacy = async (username, onSuccess, onFailure) => {
  try {
    await instance.post('/forgot-password', { username })

    return onSuccess()
  } catch (error) {
    return onFailure(error)
  }
}

export const forgotPassword = async (username) => {
  try {
    await instance.post('/forgot-password', { username })

    return { success: true, error: null }
  } catch (error) {
    return { success: false, error: extractErrors(error) }
  }
}

export const verifyRecoveryToken = async (recoveryToken) => {
  try {
    const { data: { username, policy } } = await instance.post('/verify-recovery-token', { recoveryToken })

    return { username, policy }
  } catch (error) {
    return false
  }
}

export const resetPasswordLegacy = async (recoveryToken, newPassword, onSuccess, onFailure) => {
  try {
    const { data: { idToken, status } } = await instance.post('/reset-password', { recoveryToken, newPassword })

    setIdentityIntoLocalStorage(idToken)

    return onSuccess({ idToken, status })
  } catch (error) {
    return onFailure(error.response)
  }
}

export const resetPassword = async (recoveryToken, newPassword) => {
  try {
    // const { data: { idToken, status } } = await instance.post('/reset-password', { recoveryToken, newPassword })
    const { data: { sessionToken } } = await instance.post('/reset-password', { recoveryToken, newPassword })

    oktaAuth.redirect({ sessionToken })
    // setIdentityIntoLocalStorage(idToken)

    return { success: true, error: null }
  } catch (error) {
    return { success: false, error: extractErrors(error) }
  }
}

export const webfinger = async (username) => {
  try {
    const { data: finger } = await instance.post('/webfinger', { username })
    return finger
  } catch (error) {
    console.log(error)
    return null
  }
}

export const invite = async (username, origin, role, brokerId, forceUpdate = false) => {
  try {
    const { idToken } = await getCurrentSession()
    await instance.post('/invite',
      { username, origin, role, brokerId, forceUpdate },
      {
        headers: {
          Authorization: idToken
        }
      })

    return { success: true, error: null }
  } catch (error) {
    // throw for supporting user/sagas
    throw extractErrors(error);
  }
}

export const verifyInviteToken = async (inviteToken) => {
  try {
    const { data: { username, policy } } = await instance.post('/verify-invite-token', { inviteToken })

    return { username, policy }
  } catch (error) {
    return false
  }
}

export const completeInvite = async (inviteToken, newPassword) => {
  try {
    // const { data: { idToken, status } } = await instance.post('/complete-invite', { inviteToken, newPassword })
    const { data: { sessionToken } } = await instance.post('/complete-invite', { inviteToken, newPassword })

    oktaAuth.redirect({ sessionToken })
    // setIdentityIntoLocalStorage(idToken)

    return { success: true, error: null }
  } catch (error) {
    return { success: false, error: extractErrors(error) }
  }
}


export const deleteInvite = async (username) => {
  try {
    await instance.delete('/invite', { username })
    return true
  } catch (error) {
    console.log(error)
    return false
  }
}

export const updateProfile = async (firstName, lastName, phone, phoneExt, title) => {
  if (OE_ENV.OKTA_VERSION === OKTA_VERSIONS.V2) {
    let mySession
    try {
      mySession = await oktaInstanceApiV1.get('/sessions/me')
    } catch (err) {
      console.log('session not found...')
    }
    if (mySession !== undefined && mySession !== null) {
      const { data: { login, userId, status, expiresAt }} = mySession;
      try {
        const { data: { idToken } } = await instance.post('/update-profile-idx', {
          firstName,
          lastName,
          phone,
          phoneExt,
          title,
          login,
          userId,
          status,
          expiresAt
        })

        setIdentityIntoLocalStorage(idToken)

        return { success: true, error: null }
      } catch (error) {
        let handledError
        try {
          handledError = extractValidatorAPiErrors(error)
        } catch (err) {
          handledError = extractErrors(error)
        }
        return { success: false, error: handledError }
      }
    }
    return { success: false, error: { errorSummary: 'Update profile failed' } }
  } else {
    try {
      const { data: { idToken } } = await instance.post('/update-profile', { firstName, lastName, phone, phoneExt, title })

      setIdentityIntoLocalStorage(idToken)

      return { success: true, error: null }
    } catch (error) {
      let handledError
      try {
        handledError = extractValidatorAPiErrors(error)
      } catch (err) {
        handledError = extractErrors(error)
      }
      return { success: false, error: handledError }
    }
  }
}

export const validateSession = async () => {
  if (OE_ENV.OKTA_VERSION === OKTA_VERSIONS.V2) {
    let mySession
    let accessToken
    try {
      mySession = await oktaInstanceApiV1.get('/sessions/me')
    } catch (err) {
      console.log('session not found...')
    }
    try {
      accessToken = await oktaAuth.getAccessToken();
      if (accessToken === undefined || accessToken === null) {
        const urlParams = new URLSearchParams(document.location.hash)
        accessToken = urlParams.get('access_token')
      }
      if (accessToken === undefined || accessToken === null) {
        throw Error('accessToken not retrieved...')
      }
    } catch (err) {
      console.log(err.message)
    }
    if (mySession !== undefined && mySession !== null && accessToken !== undefined && accessToken !== null) {
      try {
        const { data: { id: sessionId, login, userId, status, expiresAt }} = mySession;
        const response = await instance.post('/validate-session-idx',
          { sessionId, login, userId, status, expiresAt, accessToken })
        if (response && response.data) {
          const idxToken = response.data.idToken
          const idxStatus = response.data.status
          if (!idxToken || !idxStatus) {
            throw new Error('Error getting idToken or status')
          } else {
            setIdentityIntoLocalStorage(idxToken);
            store.dispatch(setAuth());
            return { idToken: idxToken, status: idxStatus }
          }
        }
      } catch (error) {
        console.log('signing out user...')
        await signOutIdx()
        console.log(error.message)
      }
    }
  } else {
    try {
      const { id: sessionId } = await oktaAuth._oktaAuth.session.get()
      const { data: { idToken, status } } = await instance.post('/validate-session', { sessionId })

      setIdentityIntoLocalStorage(idToken)

      store.dispatch(setAuth())

      return { idToken, status }
    } catch (error) { } // eslint-disable-line
  }

  removeIdentityFromLocalStorage()

  return { idToken: null, status: null }
}

export const getCurrentSession = async (flag = true) => {
  const userData = getUserDataFromLocalStorage()

  const expiration = new Date(get(userData, 'expiration', Date.now()))
  const delta = Math.floor(((expiration - Date.now()) / 1000) / 60)

  // If the expiration delta is about five minutes, go get a new session
  if (delta <= 5 || flag === false) {
    const { idToken } = await validateSession()
    return { idToken, userData }
  } else {
    const idToken = getIdentityFromLocalStorage()
    return { idToken, userData }
  }
}

// Wrappers around old, stupid code for obtaining default preferences and applying
// more broker-related data to the user object, because why not.

export const getUserDefaultPreferences = async (id) => {
  const api = apigClientFactory.newClient({});
  let defaultPreference;
  try {
    const { idToken } = await getCurrentSession();
    ({ data: { product_defaults: defaultPreference } } = await api.usersIdPreferencesGet(
      { id: id },
      {},
      {
        headers: {
          Authorization: idToken
        }
      }
    ));
  } catch (error) {
    defaultPreference = {};
  }
  return { defaultPreference };
}

export const getUserBrokerData = async (brokerId) => {
  const api = apigClientFactory.newClient({})
  const { idToken } = await getCurrentSession()
  let brokerage = {}
  if (brokerId) {
    ({ data: { data: brokerage } } = await api.apiGetBrokerIdGet(
      { id: brokerId },
      {},
      {
        headers: {
          Authorization: idToken
        }
      }))
  }

  const brokerName = get(brokerage, 'name', 'Argo Underwriters')
  const bundles = get(brokerage, 'bundles', [])

  return { brokerage, brokerName, bundles }
}

export const getDeductiblesData = async (stateCode) => {
  const api = apigClientFactory.newClient({})
  const { idToken } = await getCurrentSession()

  let deductibles = {}
  if (stateCode) {
    ({ data: deductibles } = await api.apiGetDeductiblesIdGet(
      { id: stateCode },
      {},
      {
        headers: {
          Authorization: idToken
        }
      })
    )
  }
  return deductibles;
}